import Papa from 'papaparse';
import { appState } from './stateManager';
import { MARTIAL_KEYS, MARTIAL_KEYS_DELIMITERS, MARTIAL_STRUCTURE } from './details';
import { CITIES } from './cities';
import pako from 'pako';

export default class DataManager {
	constructor() {}

	init() {
		this.loadDatabase();

		// this.logRecordsCsvAsJson();
	}

	async loadJson(url) {
		const res = await fetch(url);
		if (!res.ok) throw new Error(`HTTP ${res.status} for ${url}`);
		return res.json();
	}

	async loadGzip(url) {
		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`HTTP ${response.status} for ${url}`);
			}

			const buffer = await response.arrayBuffer();
			const bytes = new Uint8Array(buffer);

			let jsonString;
			// 0x1f 0x8b = gzip header; only use pako in that case
			if (bytes[0] === 0x1f && bytes[1] === 0x8b) {
				jsonString = pako.ungzip(bytes, { to: 'string' });
			} else {
				// already decompressed by the browser / server
				jsonString = new TextDecoder('utf-8').decode(bytes);
			}
			return JSON.parse(jsonString);
		} catch (error) {
			console.error('Error loading or decompressing JSON:', error);
			return null;
		}
	}

	loadDatabase() {
		Papa.parse('data.csv', {
			download: true,
			header: true,
			skipEmptyLines: true,
			delimiter: ',',
			complete: async ({ data }) => {
				if (!data) return;
				const docs = await this.loadGzip('combined_filtered.min.json.gz');
				const records = await this.loadJson('records.json');
				const processedDbData = this.getProcessedData(data, records, docs);
				const currentState = appState.getState();
				appState.setState({ ...currentState, data: processedDbData });
			},
			error: (error) => {
				console.error('Error parsing CSV:', error);
			}
		});
	}

	async loadRecords() {
		return fetch('records.json')
			.then((response) => response.json())
			.catch((error) => console.error('Error loading records:', error));
	}

	logRecordsCsvAsJson() {
		const prefix = 'https://aol.mnl.gov.hu/adatbazis/a-masodik-vilaghaboru-vesztesegi-adatbazisa/kereses?term=';
		Papa.parse('records.csv', {
			download: true,
			header: true,
			skipEmptyLines: true,
			delimiter: ',',
			complete: async ({ data }) => {
				if (!data) return;
				const obj = {};
				for (const item of data) {
					const city = item['Település'];
					const count = parseInt(item['Rekordok száma']) || 0;
					const link = item['Éles link']?.replace(prefix, '') || '';
					obj[city] = {
						count,
						link
					};
				}
				console.log(JSON.stringify(obj, null, 2));
			},
			error: (error) => {
				console.error('Error parsing CSV:', error);
			}
		});
	}

	getProcessedData(rawData, records, docs) {
		const processedDocs = this.getProcessedDocs(docs);
		const rows = rawData.map((r) => {
			const city = (r['A település mai neve'] || '').trim();
			const docs = processedDocs[city] || [];
			const id = CITIES.indexOf(city);
			const coords = this.parseNELatLng(r['GPS koordináták']);
			const start_time = this.getTime(r['A harctevékenység kezdete']);
			const end_time = this.getTime(r['A harctevékenység vége']);
			const tamado_keys = this.getMartialKeys(r['A támadó katonai erők']);
			const vedo_keys = this.getMartialKeys(r['A védő katonai erők ']);
			const tamado_numbers = this.getMartialNumbers(r['A támadó katonai erők']);
			const vedo_numbers = this.getMartialNumbers(r['A védő katonai erők ']);
			const numRecords = this.getNumRecords(r, records);
			const recordsLink = this.getRecordsLink(r, records);
			const hasAirStrike = this.hasAirStrike(r);
			return {
				...r,
				id,
				lat: coords.lat,
				lng: coords.lng,
				start_time,
				end_time,
				tamado_keys,
				vedo_keys,
				tamado_numbers,
				vedo_numbers,
				numRecords,
				recordsLink,
				hasAirStrike,
				docs
			};
		});

		// this.logCities(rows);

		return rows;
	}

	getProcessedDocs(rawDocs) {
		const processedDocs = {};
		const docHeaders = rawDocs[0];
		for (let d = 1; d < rawDocs.length; d++) {
			const doc = rawDocs[d];
			const docObj = {};
			const city = doc[1]?.trim() || '';
			if (!city) continue;
			for (let i = 0; i < docHeaders.length; i++) {
				const header = docHeaders[i];
				const value = doc[i];
				docObj[header] = value;
			}
			if (!processedDocs[city]) processedDocs[city] = [];
			let isUnique = true;
			for (const otherDoc of processedDocs[city]) {
				let isMatchingDoc = true;
				for (const key of Object.keys(otherDoc)) {
					if (key === 'Fájl neve') continue;
					if (otherDoc[key] !== docObj[key]) isMatchingDoc = false;
				}
				if (isMatchingDoc) isUnique = false;
			}
			if (isUnique) processedDocs[city].push(docObj);
		}
		return processedDocs;
	}

	logCities(rows) {
		const cities = [];
		for (const row of rows) {
			const city = row['A település mai neve'].trim();
			if (!cities.includes(city)) cities.push(city);
		}
		console.log(`['` + cities.join(`','`) + `']`);
	}

	hasAirStrike(item) {
		const data = item['Hadműveleti légitámadás időpontja és az okozott veszteség/kár'];
		const includesNumber = this.hasNumber(data);
		return includesNumber;
	}

	hasNumber(string) {
		return /\d/.test(string);
	}

	getNumRecords(item, records) {
		const normalize = (s) => (s || '').trim().replace(/,+$/, '').replace(/\s+/g, ' ');

		const name = normalize(item['A település mai neve']);
		const otherName = normalize(item['A település régi neve']);

		if (!name && !otherName) return null;

		const rec1 = records[name]?.count || 0;
		const rec2 = records[otherName]?.count || 0;

		return Math.max(rec1, rec2);
	}

	getRecordsLink(item, records) {
		const normalize = (s) => (s || '').trim().replace(/,+$/, '').replace(/\s+/g, ' ');

		const name = normalize(item['A település mai neve']);
		const otherName = normalize(item['A település régi neve']);

		const link1 = records[name]?.link || '';
		const link2 = records[otherName]?.link || '';

		if (link1) return link1;
		if (link2) return link2;
		return '';
	}

	getTime(data) {
		const timestamp = new Date(data).getTime();
		return timestamp;
	}

	getMartialKeys(data) {
		const keyGroups = [];
		const segments = this.getMartialSegments(data);
		if (!segments.length) return [];

		const nationalities = MARTIAL_STRUCTURE.nemzetiség || [];
		const abbrList = Object.keys(MARTIAL_KEYS);
		const isLetter = (ch) => !!ch && /[a-záéíóöőúüű]/i.test(ch);

		const groups = segments.map((seg) => {
			const rawChunk = seg.chunk || '';
			const chunk = rawChunk.toLowerCase();
			const matches = [];
			for (const abbr of abbrList) {
				const needle = abbr.toLowerCase();
				if (!needle) continue;

				if (this.isFalsePositiveMartialKey(rawChunk, abbr)) continue;

				let idx = chunk.indexOf(needle);
				while (idx !== -1) {
					const beforeIdx = idx - 1;
					const before = beforeIdx >= 0 ? chunk[beforeIdx] : '';

					// only if not glued to a letter on the left
					if (!isLetter(before)) {
						matches.push({
							abbr,
							value: MARTIAL_KEYS[abbr],
							index: idx
						});
					}

					idx = chunk.indexOf(needle, idx + needle.length);
				}
			}

			matches.sort((a, b) => a.index - b.index);

			// nationality
			let nationality = null;
			for (const m of matches) {
				if (nationalities.includes(m.value)) {
					nationality = m.value;
					break;
				}
			}

			const keyGroup = [];
			if (nationality) {
				keyGroup.push(nationality);
			}
			for (const m of matches) {
				const v = m.value;
				if (nationalities.includes(v)) continue;
				if (!keyGroup.includes(v)) {
					keyGroup.push(v);
				}
			}

			return {
				keyGroup,
				delimiterAfter: seg.delimiterAfter,
				nationality,
				role: null
			};
		});

		// benne
		let currentParentNationality = null;
		for (let i = 0; i < groups.length; i++) {
			const g = groups[i];

			if (g.delimiterAfter === 'benne' && g.nationality) {
				g.role = 'parent';
				currentParentNationality = g.nationality;
				continue;
			}

			if (currentParentNationality && i > 0) {
				if (!g.nationality) {
					if (!g.keyGroup.includes(currentParentNationality)) {
						g.keyGroup.unshift(currentParentNationality);
					}
					g.nationality = currentParentNationality;
					if (!g.role) g.role = 'child';
				} else if (g.nationality === currentParentNationality) {
					if (!g.role) g.role = 'child';
				} else {
					currentParentNationality = null;
				}
			}
		}

		for (const g of groups) {
			if (!g.keyGroup.length) continue;
			if (g.role === 'parent') {
				g.keyGroup.push('parent');
			} else if (g.role === 'child') {
				g.keyGroup.push('child');
			}
			keyGroups.push(g.keyGroup);
		}

		return keyGroups;
	}

	chunkHasExactAbbr(chunk, abbr) {
		if (!chunk || !abbr) return false;

		const source = chunk.toLowerCase();
		const needle = abbr.toLowerCase();

		let index = source.indexOf(needle);
		if (index === -1) return false;

		const isLetter = (ch) => !!ch && /[a-záéíóöőúüű]/i.test(ch);

		while (index !== -1) {
			const beforeIndex = index - 1;
			const before = beforeIndex >= 0 ? source[beforeIndex] : '';

			// only accept if NOT glued to a letter on the left
			// so "r." in "ezr." (before = "z") is rejected
			// but "r." at start or after space/comma/dot is ok
			if (!isLetter(before)) {
				return true;
			}

			index = source.indexOf(needle, index + 1);
		}

		return false;
	}

	isFalsePositiveMartialKey(chunk, key) {
		const source = chunk.toLowerCase();
		const keyLower = key.toLowerCase();

		const keyPos = source.indexOf(keyLower);
		if (keyPos === -1) return false;

		for (const otherKey of Object.keys(MARTIAL_KEYS)) {
			if (otherKey === key) continue;
			if (otherKey.length <= key.length) continue;

			const otherLower = otherKey.toLowerCase();
			if (!otherLower.includes(keyLower)) continue;

			const otherPos = source.indexOf(otherLower);
			if (otherPos === -1) continue;

			const keyEnd = keyPos + keyLower.length;
			const otherEnd = otherPos + otherLower.length;

			// key fully inside a longer abbreviation present in the same chunk
			if (keyPos >= otherPos && keyEnd <= otherEnd) {
				return true;
			}
		}

		return false;
	}

	getMartialSegments(data) {
		const dataString = data?.toString().toLowerCase() || '';
		if (!dataString.length) return [];

		const escaped = MARTIAL_KEYS_DELIMITERS.map((d) => d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
		const regex = new RegExp(`\\s*(${escaped.join('|')})\\s*`, 'gi');

		const segments = [];
		let lastIndex = 0;
		let match;

		while ((match = regex.exec(dataString)) !== null) {
			const chunk = dataString.slice(lastIndex, match.index).trim();
			const delimiter = (match[1] || '').toLowerCase();
			if (chunk) {
				segments.push({ chunk, delimiterAfter: delimiter });
			}
			lastIndex = regex.lastIndex;
		}

		const tail = dataString.slice(lastIndex).trim();
		if (tail) {
			segments.push({ chunk: tail, delimiterAfter: null });
		}

		return segments;
	}

	getMartialNumbers(data) {
		const segments = this.getMartialSegments(data);
		if (!segments.length) return [];

		const numbers = [];

		for (const seg of segments) {
			const chunk = seg.chunk || '';
			if (!chunk) {
				numbers.push(null);
				continue;
			}

			const parts = chunk.split('.');
			let numValue = null;

			for (const part of parts) {
				const trimmed = part.trim();
				if (!trimmed) continue;
				const n = parseInt(trimmed, 10);
				if (!Number.isNaN(n)) {
					numValue = n;
					break;
				}
			}

			numbers.push(numValue);
		}

		return numbers;
	}

	splitByDelimiters(str, delimiters) {
		const escaped = delimiters.map((d) => d.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
		const regex = new RegExp(escaped.join('|'), 'g');

		return str
			.split(regex)
			.map((s) => s.trim())
			.filter(Boolean);
	}

	parseNELatLng(s) {
		const m = /([NS])\s*([0-9]+(?:\.\d+)?)\s*,\s*([EW])\s*([0-9]+(?:\.\d+)?)/i.exec(s ?? '');
		if (!m) return { lat: 0, lng: 0 };
		let lat = parseFloat(m[2]),
			lng = parseFloat(m[4]);
		if (m[1].toUpperCase() === 'S') lat = -lat;
		if (m[3].toUpperCase() === 'W') lng = -lng;
		return { lat, lng };
	}
}
